import {
    Suspense,
    useState,
    useEffect,
    useRef,
    lazy,
    useContext,
    createContext,
} from '@wordpress/element';
import { defaultSort, filterTypes } from '../config';
import {
    brandIdLookup,
    cleanMenuFilterForQuery,
    getInitialMenuFilter,
    getOrderText,
    toLowerWithDashes,
    verifiedParams,
    formatCategory,
    consoleDebug,
} from '../util';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import useFetchRest from '../hooks/useFetchRest';
import { ErrorBoundary } from '@sentry/react';
import ErrorComponent from '../ErrorComponent';
import { RetailerIdContext } from '../App';
import { ConfigContext } from '../index.js';
import {
    useCartData,
    useMenuProducts,
    useSpecialsList,
} from '../hooks/dutchie-queries.js';
import isEqual from 'lodash.isequal';
import usePricingType from '../hooks/usePricingType.js';
import SingleProduct from './SingleProduct.jsx';
import { ProductPopupContext } from '../Shortcode.jsx';

const Products = lazy(() => import('../components/Products/Products'));
const Specials = lazy(() => import('../components/Products/Specials'));
const StoreNotice = lazy(() => import('../components/Menu/StoreNotice.jsx'));
const MenuBanners = lazy(() => import('../components/Menu/MenuBanners'));
const Filter = lazy(() => import('../components/Filter/Filter'));
const Surfside = lazy(() => import('../components/Surfside/Surfside'));
const LoadingSpinner = lazy(() => import('../components/LoadingSpinner'));
const FilterNavbar = lazy(() => import('../components/Filter/FilterNavbar'));
const ProductNav = lazy(() => import('../components/Menu/ProductNav'));

export const PriceRangeContext = createContext(null);
export const QueryArgsContext = createContext(null);

const Menu = ({
    rootId,
    forcedOrderType,
}) => {
    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [specialProductsIds, setSpecialProductsIds] = useState({});
    const [specialBrands, setSpecialBrands] = useState([]);
    const [filteredSpecialProductsIds, setFilteredSpecialProductsIds] = useState({});

    const globalConfig = useContext(ConfigContext);

    const {
        styleOptions,
        surfside,
        analytics,
        menuConfig: {
            paginationLimit,
            productDisplayStyle: initialDisplayStyle,
            numColumns,
            menuCategoriesToShow,
            hideCategoryFilter,
            hideSubcategoryFilter,
            disableMobileInfiniteScroll,
            persistMenuBanners,
            hideTagsFilter,
            showDeliveryPickup,
            showEmptySpecials,
        },
        menuFilter,
        retailers,
        tagSlugs,
    } = globalConfig;

    const retailerId = useContext(RetailerIdContext);
    const { productPopupData, setProductPopupData } = useContext(ProductPopupContext);

    const { data: cartData, isFetched: isFetchedCartData } = useCartData(retailerId);
    const [pricingType] = usePricingType(retailerId);

    const { data: menuData, isFetched: isFetchedMenuData } = useFetchRest(
        `data/menu?retailerId=${retailerId}`
    );

    const { data: apiSpecialsList, isLoading: isLoadingSpecials } = useSpecialsList(retailerId);
    const [displaySpecialsList, setDisplaySpecialsList] = useState(false);
    const [selectedSpecial, setSelectedSpecial] = useState(null);
    const [filteredSpecialsList, setFilteredSpecialsList] = useState([]);

    // const { data: specialsList } = useSpecialsList(retailerId);

    // const specialsExist = Array.isArray(specialsList) && specialsList?.length > 0;
    const specialsExist = Array.isArray(apiSpecialsList) && apiSpecialsList?.length > 0;
    const [selectedSpecialId, setSelectedSpecialId] = useState(null);

    // Add a debug console log to see what's coming from the API:
    useEffect(() => {
        if (apiSpecialsList) {
            console.log('🔴 Direct API specials data:', apiSpecialsList);
            setFilteredSpecialsList(apiSpecialsList);
        }
    }, [apiSpecialsList]);

    const filterValidSpecialProducts = (specialProductsIds, specialsList) => {
        consoleDebug('💈 filterValidSpecialProducts Debug - Start');
        if (!specialProductsIds || !specialsList) return {};

        // Get counts using Object.keys()
        const unfilteredCount = Object.keys(specialProductsIds).length;
        consoleDebug('Number of unfiltered special products:', unfilteredCount);

        if (showEmptySpecials) {
            consoleDebug('Not applying filter, returning:', specialProductsIds);
            return specialProductsIds;
        }

        // Otherwise, apply the original filtering logic
        const validSpecialIds = new Set(specialsList.map(special => special.id));
        const filteredSpecialsObject = Object.fromEntries(
            Object.entries(specialProductsIds).filter(([specialId]) => validSpecialIds.has(specialId))
        );

        // Get count of filtered items
        const filteredCount = Object.keys(filteredSpecialsObject).length;

        consoleDebug('Unfiltered valid products list:', specialProductsIds, `(${unfilteredCount} items)`);
        consoleDebug('Applying filter to specials:', filteredSpecialsObject, `(${filteredCount} items)`);
        consoleDebug('Items removed by filter:', unfilteredCount - filteredCount);
        consoleDebug('💈 filterValidSpecialProducts Debug - End');

        return filteredSpecialsObject;
    };


    useEffect(() => {
        if (menuData && isFetchedMenuData) {
            setSpecialProductsIds(menuData.specialProductsIds || {});
            setSpecialBrands(menuData.specialBrands || []);
        }
    }, [menuData, isFetchedMenuData]);

    useEffect(() => {
        if (apiSpecialsList && specialProductsIds) {
            // const filtered = filterValidSpecialProducts(specialProductsIds, specialsList);
            const filtered = filterValidSpecialProducts(specialProductsIds, apiSpecialsList);
            setFilteredSpecialProductsIds(filtered);
        }
    }, [apiSpecialsList, specialProductsIds]);

    /**
     * Amount of products to fetch in the background for each query.
     * Displayed products will depend on `paginationLimit`.
     */
    const initialCacheLimit = 100;
    const [productCacheLimit, setProductCacheLimit] =
        useState(initialCacheLimit);
    const [productDisplayFactor, setProductDisplayFactor] = useState(1);

    /**
     * This is needed to deal with `handleScroll` being called from an event
     * listener. Without it, it wouldn't receive updated state.
     */
    const displayFactorRef = useRef(productDisplayFactor);
    const setDisplayFactorRef = (factor) => {
        displayFactorRef.current = factor;
        setProductDisplayFactor(factor);
    };
    const cacheLimitRef = useRef(productCacheLimit);
    const setCacheLimitRef = (limit) => {
        cacheLimitRef.current = limit;
        setProductCacheLimit(limit);
    };

    const {
        showMenuBannerSurfside,
        showMenuBanner,
        menuBanners,
        menuSlug,
        seoGeos,
        retailerName,
        niceName,
        brandTerms,
        showStoreNotice,
        storeNoticeContent
    } = retailers[retailerId];

    const filterTerms = {
        ...globalConfig.filterTerms,
        brands: brandTerms,
    };

    const searchParams = new URLSearchParams(location.search);
    const searchQuery = searchParams.get('search');
    const thcMin = searchParams.get('thcMin');

    const [selectedFilters, setSelectedFilters] = useState(() => {
        const initialFilters = getInitialMenuFilter(params, filterTerms, menuFilter, searchQuery, thcMin);
        return initialFilters;
    });

    const [sort, setSort] = useState(defaultSort);
    const [queryArgs, setQueryArgs] = useState(null);
    const [priceRange, setPriceRange] = useState(null);

    const orderText = getOrderText(selectedFilters, filterTerms);

    /**
     * Assign display style to a state so it can be toggled.
     */
    const [displayStyle, setDisplayStyle] = useState(initialDisplayStyle);

    /**
     * Set products query arguments.
     */
    function getAndSetQueryArgs() {
        const { sortDir, sortKey } = sort;

        // Get the menuType from forcedOrderType
        const menuTypeParam = forcedOrderType?.menuType;

        // Determine the effective pricing type based on URL parameter
        let effectivePricingType = cartData?.pricingType; // Default to cart's pricing type

        // Override with URL parameter if present
        if (menuTypeParam) {
            if (menuTypeParam.toLowerCase() === "rec") {
                effectivePricingType = "RECREATIONAL";
            } else if (menuTypeParam.toLowerCase() === "med") {
                effectivePricingType = "MEDICAL";
            }
        }

        let args = null;
        if (selectedFilters?.custom_section) {
            args = {
                queryName: 'MenuByCustomSection',
                variables: {
                    retailerId,
                    limit: paginationLimit,
                    menuSectionFilterType: 'CUSTOM_SECTION',
                    menuSectionFilterName: selectedFilters.custom_section,
                    menuType: effectivePricingType,
                    sortDir,
                    sortKey,
                },
            };
        } else if (selectedFilters?.staff_picks) {
            args = {
                queryName: 'MenuByStaffPicks',
                variables: {
                    retailerId,
                    limit: paginationLimit,
                    menuSectionFilterType: 'STAFF_PICKS',
                    menuType: effectivePricingType,
                    sortDir,
                    sortKey,
                },
            };

            setDisplaySpecialsList(false);
        } else if (selectedFilters?.specials) {
            if (specialsExist) {
                if (selectedSpecialId) {
                    setSelectedSpecial(
                        apiSpecialsList.find(
                            (special) => special.id === selectedSpecialId
                        )
                    );
                } else {
                    setSelectedSpecial(null);
                }

                args = {
                    queryName: 'MenuBySpecialsSection',
                    variables: {
                        retailerId,
                        limit: paginationLimit,
                        menuFilter: {
                            ...cleanMenuFilterForQuery(
                                selectedFilters,
                                menuFilter,
                                tagSlugs?.[retailerId]
                            ),
                            menuSection: {
                                type: 'SPECIALS',
                                specialId:
                                    selectedSpecialId ||
                                    apiSpecialsList.map((special) => special.id),
                            },
                        },
                        menuType: cartData?.pricingType,
                        sortDir,
                        sortKey,
                    },
                };

                setDisplaySpecialsList(
                    Boolean(specialsExist && !params?.specialCategoryName)
                );
            }
        } else if (selectedFilters) {
            args = {
                queryName: 'MenuQuery',
                variables: {
                    retailerId,
                    limit: paginationLimit,
                    menuFilter: cleanMenuFilterForQuery(
                        selectedFilters,
                        menuFilter,
                        tagSlugs?.[retailerId]
                    ),
                    menuType: effectivePricingType,
                    sortDir,
                    sortKey,
                },
            };
            setDisplaySpecialsList(false);
        }

        if (args) {
            consoleDebug('🔍 Complete Query Arguments:', {
                queryName: args.queryName,
                menuFilter: args.variables.menuFilter,
                menuType: args.variables.menuType,
                pricingType: effectivePricingType,
                timestamp: new Date().toISOString()
            });
        }
        setQueryArgs(args);
    }

    /**
     * Menu/products query.
     */
    const {
        data: productsData,
        fetchNextPage: fetchNextPageProductsData,
        refetch: refetchProductsData,
        isFetchingNextPage: isFetchingNextPageProductsData,
        hasNextPage: hasNextPageProductsData,
    } = useMenuProducts(retailerId, queryArgs, {
        enabled: Boolean(queryArgs !== null && isFetchedMenuData),
        paginationLimit,
        productCacheLimit,
    });

    /**
     * Calculate and set min and max price ranges of queried products.
     */
    useEffect(() => {
        if (productsData?.pages) {
            const products = productsData.pages.map((page) => page.data).flat();
            const prices = products
                .map((product) =>
                    product.variants.map((variant) =>
                        variant?.[pricingType.special]
                            ? variant?.[pricingType.special]
                            : variant?.[pricingType.standard]
                    )
                )
                .flat();

            const range = {
                min: Math.floor(Math.min(...prices)),
                max: Math.ceil(Math.max(...prices)),
            };

            setPriceRange(range);
        }
    }, [productsData]);

    /**
     * Updates products query arguments when filters, pricingType, sort, or selected special is updated.
     * Resets pagination offset.
     */
    useEffect(() => {
        if (!isFetchedCartData || !cartData?.pricingType) return;

        getAndSetQueryArgs();
    }, [
        isFetchedCartData,
        selectedFilters,
        sort,
        selectedSpecialId,
        apiSpecialsList,
        cartData?.pricingType,
    ]);

    /**
     * Refetch products after queryArgs are updated.
     */
    useEffect(() => {
        if (!queryArgs) return;

        refetchProductsData();
    }, [queryArgs]);

    /**
     * Make sure the specials cards are hidden when viewing filtered specials.
     */
    useEffect(() => {
        if (params?.specialCategoryName) {
            setDisplaySpecialsList(false);
        } else if (selectedFilters.specials) {
            setDisplaySpecialsList(specialsExist);
        }
    }, [params]);

    /**
     * Use scroll to fetch next page of product data.
     * Only apply on screens > 575px.
     * @returns void
     */
    const loadMoreButtonRef = useRef(null);
    const [isWideScreen, setIsWideScreen] = useState(window.innerWidth > 575);

    function handleScroll() {
        if (!isWideScreen && disableMobileInfiniteScroll) {
            return;
        }
        const loadMoreButton = loadMoreButtonRef.current;
        if (!loadMoreButton) return;

        const { top } = loadMoreButton.getBoundingClientRect();
        const isButtonVisible = top - window.innerHeight <= 100;

        if (isButtonVisible) {
            if (
                displayFactorRef.current * paginationLimit <
                cacheLimitRef.current
            ) {
                setDisplayFactorRef(displayFactorRef.current + 1);
            } else {
                fetchNextPageProductsData();
                setCacheLimitRef(cacheLimitRef.current + initialCacheLimit);
            }
        }
    }

    /**
     * Debounce `handleScroll`. Attach to scroll listener.
     */
    let debounceTimer = null;
    function debounceScroll() {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            handleScroll();
        }, 300);
    }

    /**
     * Attach (and remove) scroll event listener.
     */
    useEffect(() => {
        window.addEventListener('scroll', debounceScroll);

        return () => window.removeEventListener('scroll', debounceScroll);
    }, []);

    /**
     * Handle window resize
     */
    function handleResize() {
        setIsWideScreen(window.innerWidth > 575);
    }

    /**
     * Attach (and remove) scroll and resize event listeners.
     */
    useEffect(() => {
        const debouncedScroll = debounceScroll();
        window.addEventListener('scroll', debouncedScroll);
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('scroll', debouncedScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [isWideScreen]);

    /**
     * Update the document title with relevant information (category, brand, retailer)
     */
    useEffect(() => {
        const categoryFilter = selectedFilters[filterTypes.category]
            ? formatCategory(selectedFilters[filterTypes.category])
            : '';

        let brandName = '';
        if (
            selectedFilters.brandIds &&
            Array.isArray(selectedFilters.brandIds) &&
            selectedFilters.brandIds.length > 0
        ) {
            brandName =
                filterTerms.brands?.find(
                    (brand) => brand && brand.id === selectedFilters.brandIds[0]
                )?.name || '';
        }

        let title;
        if (brandName) {
            title = `Shop ${brandName} | ${seoGeos ? `${seoGeos} | ` : ''
                }${retailerName}`;
        } else if (categoryFilter) {
            title = `Shop ${categoryFilter} | ${seoGeos ? `${seoGeos} | ` : ''
                }${retailerName}`;
        } else {
            title = `Order Cannabis Online ${seoGeos ? `in ${seoGeos} ` : ''
                } | ${retailerName}`;
        }

        document.title = title;
    }, [retailerName, seoGeos, selectedFilters, filterTerms.brands]);

    /**
     * Set selected filters based on routing params.
     */
    useEffect(() => {
        let setFilters = true;

        if (location?.state?.setFilters === false) {
            setFilters = false;
        }

        if (setFilters) {
            if (params?.categoryType) {
                switch (params?.categoryType.toLowerCase()) {
                    case 'special':
                        if (params?.categoryName) {
                            setSelectedSpecialId(params?.categoryName);

                            setSelectedFilters({
                                ...menuFilter,
                                specials: true,
                            });
                        }
                        break;
                    case 'specials':
                        if (
                            params?.categoryName &&
                            params?.specialCategoryName
                        ) {
                            switch (params.categoryName) {
                                case 'category':
                                    const [filterType, filterName] =
                                        verifiedParams(
                                            params.categoryName,
                                            params.specialCategoryName,
                                            filterTerms
                                        );

                                    if (filterType && filterName) {
                                        setSelectedFilters({
                                            ...menuFilter,
                                            specials: true,
                                            [filterType]: filterName,
                                        });
                                    }
                                    break;
                                case 'brand':
                                    const brandId = brandIdLookup(
                                        params.specialCategoryName,
                                        filterTerms.brands
                                    );

                                    if (brandId) {
                                        setSelectedFilters({
                                            ...menuFilter,
                                            brandIds: [brandId],
                                            specials: true,
                                        });
                                    }
                                    break;
                                default:
                                    break;
                            }
                        } else {
                            setSelectedFilters({
                                ...menuFilter,
                                specials: true,
                            });
                        }
                        break;
                    case 'brand':
                        if (params?.categoryName) {
                            const brandId = brandIdLookup(
                                params.categoryName,
                                filterTerms.brands
                            );
                            if (brandId) {
                                setSelectedFilters({
                                    ...menuFilter,
                                    brandIds: [brandId],
                                });
                            } else {
                                navigate(`/shop/${params.locationSlug}/`);
                            }
                        }
                        break;
                    case 'tags':
                        if (params?.categoryName) {
                            setSelectedFilters({
                                ...menuFilter,
                                tags: [params.categoryName],
                            });
                        } else {
                            navigate(`/shop/${params.locationSlug}/`);
                        }
                        break;
                    case 'effect':
                        if (params?.categoryName) {
                            setSelectedFilters({
                                ...menuFilter,
                                effects: [params.categoryName.toUpperCase()],
                            });
                        } else {
                            navigate(`/shop/${params.locationSlug}/`);
                        }
                        break;
                    case 'subcategory':
                        if (params?.categoryName) {
                            setSelectedFilters({
                                ...menuFilter,
                                subcategory: params.categoryName.toUpperCase(),
                            });
                        } else {
                            navigate(`/shop/${params.locationSlug}/`);
                        }
                        break;
                    default:
                        if (params?.categoryName) {
                            const [filterType, filterName] = verifiedParams(
                                params.categoryType,
                                params.categoryName,
                                filterTerms
                            );

                            if (filterType && filterName) {
                                let newFilters = {
                                    ...menuFilter,
                                    [filterType]: filterName,
                                };

                                if (thcMin) {
                                    newFilters.potencyThc = {
                                        ...newFilters.potencyThc,
                                        min: parseFloat(thcMin)
                                    };
                                }

                                setSelectedFilters(newFilters);
                            } else {
                                navigate(`/shop/${params.locationSlug}/`);
                            }
                        } else {
                            navigate(`/shop/${params.locationSlug}/`);
                        }
                        break;
                }
            }
        }
    }, [params]);

    /**
     * Update URL when filters change.
     */
    useEffect(() => {
        function brandsNavigate() {
            if (!selectedFilters) return;
            // handle if second or more brand is selected while specials is toggled.
            if (
                selectedFilters.specials &&
                specialBrands &&
                selectedFilters.brandIds.length > 1
            ) {
                const filteredSpecialBrands = specialBrands
                    .filter((brand) =>
                        selectedFilters.brandIds.includes(brand.id)
                    )
                    .map((brand) => brand.id);

                navigate(`/shop/${params.locationSlug}/specials`, {
                    state: { setFilters: false },
                });

                // only update filters and navigate if needed.
                if (!isEqual(filteredSpecialBrands, selectedFilters.brandIds)) {
                    setSelectedFilters({
                        ...menuFilter,
                        brandIds: filteredSpecialBrands,
                        specials: true,
                    });
                }

                return;
            }

            const brandName = Array.isArray(filterTerms?.brands)
                ? filterTerms.brands.find(
                    (brand) => selectedFilters.brandIds[0] === brand.id
                )?.name
                : undefined;

            if (!brandName) {
                if (selectedFilters.category) {
                    categoryNavigate();
                }

                return;
            }

            const path = `/shop/${params.locationSlug}/${selectedFilters.specials ? 'specials' : 'brand'
                }/${selectedFilters.specials
                    ? 'brand/' + toLowerWithDashes(brandName)
                    : toLowerWithDashes(brandName)
                }/`;

            navigate(path, { state: { setFilters: false } });
        }

        function categoryNavigate() {
            if (!selectedFilters) return;

            // stay on brand URL if filtering further.
            if (
                params.categoryType === 'brand' &&
                selectedFilters[filterTypes.brands]?.length > 0
            ) {
                return;
            }

            // handle if second or more brand is selected while specials is toggled.
            if (
                selectedFilters.specials &&
                specialBrands &&
                selectedFilters.brandIds &&
                selectedFilters.brandIds.length > 0
            ) {
                const filteredSpecialBrands = specialBrands
                    .filter((brand) =>
                        selectedFilters.brandIds.includes(brand.id)
                    )
                    .map((brand) => brand.id);

                // only update filters and navigate if needed.
                if (!isEqual(filteredSpecialBrands, selectedFilters.brandIds)) {
                    setSelectedFilters({
                        ...selectedFilters,
                        brandIds: filteredSpecialBrands,
                    });
                }

                return;
            }

            let path = `/shop/${params.locationSlug}/`;

            if (selectedFilters.specials) {
                path += 'specials';
                if (selectedFilters[filterTypes.category]) {
                    path += `/category/${toLowerWithDashes(
                        selectedFilters[filterTypes.category]
                    )}`;
                }
            } else if (selectedFilters[filterTypes.category]) {
                path += `category/${toLowerWithDashes(
                    selectedFilters[filterTypes.category]
                )}`;
            }

            navigate(path, { state: { setFilters: false } });
        }

        if (
            selectedFilters[filterTypes.category] ||
            selectedFilters[filterTypes.brands]?.length > 0
        ) {
            if (params?.categoryType) {
                if (
                    (params.categoryType === 'brand' ||
                        (params.categoryType === 'effect' &&
                            selectedFilters[filterTypes.effects].length ===
                            0) ||
                        params.categoryType === 'specials' ||
                        params.categoryType === 'special') &&
                    selectedFilters[filterTypes.brands]
                ) {
                    brandsNavigate();
                } else if (
                    (params.categoryType === 'category' ||
                        (params.categoryType === 'effect' &&
                            selectedFilters[filterTypes.effects].length ===
                            0) ||
                        params.categoryType === 'specials' ||
                        params.categoryType === 'special') &&
                    selectedFilters[filterTypes.category]
                ) {
                    categoryNavigate();
                } else if (
                    params.categoryType === 'brand' &&
                    !selectedFilters[filterTypes.brands]?.length > 0
                ) {
                    if (selectedFilters[filterTypes.category]) {
                        categoryNavigate();
                    } else {
                        navigate(`/shop/${params.locationSlug}/`, {
                            state: { setFilters: false },
                        });
                    }
                } else if (
                    params.categoryType === 'category' &&
                    !selectedFilters[filterTypes.category]
                ) {
                    if (selectedFilters[filterTypes.brands]?.length > 0) {
                        brandsNavigate();
                    } else {
                        navigate(`/shop/${params.locationSlug}/`, {
                            state: { setFilters: false },
                        });
                    }
                }
            } else {
                if (selectedFilters[filterTypes.category]) {
                    categoryNavigate();
                } else if (selectedFilters[filterTypes.brands]?.length > 0) {
                    brandsNavigate();
                }
            }
        } else if (selectedFilters[filterTypes.specials]) {
            if (selectedSpecialId) {
                navigate(
                    `/shop/${params.locationSlug}/special/${selectedSpecialId}/`,
                    {
                        state: { setFilters: false },
                    }
                );
            } else {
                if (params?.categoryType !== 'special') {
                    categoryNavigate();
                }
            }
        } else if (selectedFilters[filterTypes.effects]?.length > 0) {
            navigate(
                `/shop/${params.locationSlug}/effect/${selectedFilters[
                    filterTypes.effects
                ][0].toLowerCase()}/`,
                {
                    state: { setFilters: false },
                }
            );
        } else if (selectedFilters[filterTypes.subcategory]) {
            navigate(
                `/shop/${params.locationSlug}/subcategory/${selectedFilters[
                    filterTypes.subcategory
                ].toLowerCase()}/`,
                {
                    state: { setFilters: false },
                }
            );
        } else if (selectedFilters[filterTypes.tags]?.length > 0) {
            navigate(
                `/shop/${params.locationSlug}/tags/${encodeURIComponent(
                    selectedFilters[filterTypes.tags][0]
                )}/`,
                {
                    state: { setFilters: false },
                }
            );
        } else {
            if (params?.categoryType !== 'special') {
                navigate(`/shop/${params.locationSlug}/`, {
                    state: { setFilters: false },
                });
            }
        }
    }, [selectedSpecialId, selectedFilters, specialBrands]);

    /**
     * Filter out specials that have no products.
     */
    useEffect(() => {
        if (apiSpecialsList) {
            console.log('Using direct API specials data:', apiSpecialsList.length);
            setFilteredSpecialsList(apiSpecialsList);
        }
    }, [apiSpecialsList]);

    consoleDebug('🛍️ Menu Data Debug - Start');
    consoleDebug({
        productsData,
        productCacheLimit,
        productDisplayFactor,
        paginationLimit,
        productsDisplayed: productDisplayFactor * paginationLimit,
    });
    consoleDebug('🛍️ Menu Data Debug Debug - End');

    useEffect(() => {
        consoleDebug('🌿 Menu Component - forcedOrderType Debug:', {
            forcedOrderType,
            isObject: typeof forcedOrderType === 'object',
            menuType: forcedOrderType?.menuType,
            allProps: forcedOrderType ? Object.keys(forcedOrderType) : 'none'
        });
    }, [forcedOrderType]);

    useEffect(() => {
        if (specialsExist && selectedSpecialId && apiSpecialsList) {
            const special = apiSpecialsList.find(
                (special) => special.id === selectedSpecialId
            );
            setSelectedSpecial(special || null);
        } else {
            setSelectedSpecial(null);
        }
    }, [selectedSpecialId, apiSpecialsList, specialsExist]);

    return (
        <>
            <PriceRangeContext.Provider value={{ priceRange, setPriceRange }}>
                <ErrorBoundary
                    showDialog
                    fallback={(props) => <ErrorComponent {...props} />}
                    beforeCapture={(scope) => {
                        scope.setTag('component', 'FilterNavbar');
                        scope.setTag('parent', 'Menu');
                    }}
                >
                    <Suspense fallback={<LoadingSpinner />}>
                        <FilterNavbar
                            filterTerms={filterTerms}
                            selectedFilters={selectedFilters}
                            setSelectedFilters={setSelectedFilters}
                            styleOptions={styleOptions}
                            filterCategories={menuCategoriesToShow}
                            specialsExist={specialsExist}
                            menuSlug={menuSlug}
                            forcedOrderType={forcedOrderType}
                        />
                    </Suspense>
                </ErrorBoundary>
                {showMenuBanner && (() => {
                    // Check if category filter should hide banner
                    const shouldHideForCategory = selectedFilters?.category &&
                        (!persistMenuBanners || !persistMenuBanners.includes('categories'));

                    // Check if brands filter should hide banner
                    const shouldHideForBrands = selectedFilters?.brandIds?.length > 0;

                    // Check if specials filter should hide banner
                    const shouldHideForSpecials = selectedFilters?.specials &&
                        (!persistMenuBanners || !persistMenuBanners.includes('specials'));

                    // Only hide banner if any non-persisted filter is active
                    return !shouldHideForCategory && !shouldHideForBrands && !shouldHideForSpecials;
                })() ? (
                    <ErrorBoundary
                        showDialog
                        fallback={(props) => <ErrorComponent {...props} />}
                        beforeCapture={(scope) => {
                            scope.setTag('component', 'MenuBanners');
                            scope.setTag('parent', 'Menu');
                        }}
                    >
                        <Suspense fallback={<LoadingSpinner />}>
                            <MenuBanners
                                banners={menuBanners}
                                rootId={rootId}
                                setSelectedFilters={setSelectedFilters}
                                surfsideBanner={showMenuBannerSurfside}
                                surfside={surfside}
                                styleOptions={styleOptions}
                                hideSubcategoryFilter={hideSubcategoryFilter}
                                hideCategoryFilter={hideCategoryFilter}
                                hideTagsFilter={hideTagsFilter}
                                specialsExist={specialsExist}
                            />
                        </Suspense>
                    </ErrorBoundary>
                ) : null}
                {surfside?.enable &&
                    surfside.showMenuBanner &&
                    !showMenuBannerSurfside ? (
                    <Suspense fallback={<LoadingSpinner />}>
                        <Surfside
                            placement="menu-banner"
                            accountId={surfside.account_id}
                            sourceId={surfside.source_id}
                            placementId={surfside.placement_ids.menu_banner}
                        />
                    </Suspense>
                ) : null}
                {showStoreNotice && <StoreNotice content={storeNoticeContent} />}
                <div
                    className={`${styleOptions?.primaryMenuClasses
                        ? 'headless-shop-container headless-menu ' +
                        styleOptions.primaryMenuClasses
                        : 'headless-shop-container headless-menu'
                        }`}
                    id="shop"
                >
                    {filterTerms && selectedFilters ? (
                        <QueryArgsContext.Provider value={queryArgs}>
                            <ErrorBoundary
                                showDialog
                                fallback={(props) => (
                                    <ErrorComponent {...props} />
                                )}
                                beforeCapture={(scope) => {
                                    scope.setTag('component', 'Filter');
                                    scope.setTag('parent', 'Menu');
                                }}
                            >
                                <Suspense fallback={<LoadingSpinner />}>
                                    <Filter
                                        filterTerms={filterTerms}
                                        selectedFilters={selectedFilters}
                                        setSelectedFilters={setSelectedFilters}
                                        styleOptions={styleOptions}
                                        categoriesToShow={menuCategoriesToShow}
                                        hideCategoryFilter={hideCategoryFilter}
                                        hideSubcategoryFilter={
                                            hideSubcategoryFilter
                                        }
                                        specialsExist={specialsExist}
                                        hideTagsFilter={hideTagsFilter}
                                        showDeliveryPickup={showDeliveryPickup}
                                        forcedOrderType={forcedOrderType}
                                        thcMin={thcMin}
                                    />
                                </Suspense>
                            </ErrorBoundary>
                        </QueryArgsContext.Provider>
                    ) : null}

                    <div className={'headless-products-container has-filter'}>
                        {surfside?.enable &&
                            surfside.showCategoryBanner &&
                            selectedFilters.category ? (
                            <Suspense fallback={<LoadingSpinner />}>
                                <Surfside
                                    placement="category-banner"
                                    accountId={surfside.account_id}
                                    sourceId={surfside.source_id}
                                    placementId={
                                        surfside.placement_ids.menu_banner
                                    }
                                    category={selectedFilters.category}
                                />
                            </Suspense>
                        ) : null}
                        <ErrorBoundary
                            showDialog
                            fallback={(props) => <ErrorComponent {...props} />}
                            beforeCapture={(scope) => {
                                scope.setTag('component', 'ProductNav');
                                scope.setTag('parent', 'Menu');
                            }}
                        >
                            <Suspense fallback={<LoadingSpinner />}>
                                <ProductNav
                                    selectedFilters={selectedFilters}
                                    filterTerms={filterTerms}
                                    sort={sort}
                                    setSort={setSort}
                                    display={displayStyle}
                                    setDisplayStyle={setDisplayStyle}
                                    initialDisplayStyle={initialDisplayStyle}
                                    searchQuery={searchQuery}
                                />
                            </Suspense>
                        </ErrorBoundary>
                        {displaySpecialsList && (
                            <ErrorBoundary
                                showDialog
                                fallback={(props) => (
                                    <ErrorComponent {...props} />
                                )}
                                beforeCapture={(scope) => {
                                    scope.setTag('component', 'Specials');
                                    scope.setTag('parent', 'Menu');
                                }}
                            >
                                <Suspense fallback={<LoadingSpinner />}>
                                    <Specials
                                        selectedSpecial={selectedSpecial}
                                        specials={filteredSpecialsList}
                                        setSelectedSpecialId={setSelectedSpecialId}
                                    />
                                </Suspense>
                            </ErrorBoundary>
                        )}
                        <div
                            className={`${displayStyle === 'list'
                                ? 'headless-products-list'
                                : `headless-products-row headless-columns-${numColumns}`
                                } `}
                        >
                            {productsData ? (
                                <ErrorBoundary
                                    showDialog
                                    fallback={(props) => (
                                        <ErrorComponent {...props} />
                                    )}
                                    beforeCapture={(scope) => {
                                        scope.setTag('component', 'Products');
                                        scope.setTag('parent', 'Menu');
                                    }}
                                >
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <Products
                                            products={productsData.pages
                                                .flatMap((page) => page.data)
                                                .slice(
                                                    0,
                                                    productDisplayFactor *
                                                    paginationLimit
                                                )}
                                            display={displayStyle}
                                            initialDisplayStyle={initialDisplayStyle}
                                            setDisplayStyle={setDisplayStyle}
                                            selectedFilters={selectedFilters}
                                            surfside={surfside}
                                            menuSlug={menuSlug}
                                            setSelectedFilters={
                                                setSelectedFilters
                                            }
                                            gtmId={analytics.gtm_id}
                                            specialProductsIds={
                                                filteredSpecialProductsIds
                                            }
                                            specialsExist={specialsExist}
                                            specialsList={apiSpecialsList}
                                            isMenu={true}
                                            paginationLimit={
                                                paginationLimit *
                                                productsData.pages.length
                                            }
                                            pricingType={cartData?.pricingType}
                                        />
                                    </Suspense>
                                </ErrorBoundary>
                            ) : selectedFilters.specials && !specialsExist ? (
                                <>
                                    Sorry, but there are no specials currently
                                    in stock. Check back soon!
                                </>
                            ) : (
                                <LoadingSpinner loadingType="list" />
                            )}
                        </div>
                        {productsData ? (
                            <ErrorBoundary
                                showDialog
                                fallback={(props) => (
                                    <ErrorComponent {...props} />
                                )}
                                beforeCapture={(scope) => {
                                    scope.setTag('component', 'ShowMore');
                                    scope.setTag('parent', 'Menu');
                                }}
                            >
                                <Suspense fallback={<LoadingSpinner />}>
                                    {isFetchingNextPageProductsData ? (
                                        <LoadingSpinner />
                                    ) : null}
                                    {hasNextPageProductsData ? (
                                        <button
                                            ref={loadMoreButtonRef}
                                            className="headless-button headless-button-small headless-load-more"
                                            onClick={() => {
                                                if (
                                                    displayFactorRef.current *
                                                    paginationLimit <
                                                    cacheLimitRef.current
                                                ) {
                                                    setDisplayFactorRef(
                                                        displayFactorRef.current +
                                                        1
                                                    );
                                                } else {
                                                    fetchNextPageProductsData();
                                                    setCacheLimitRef(
                                                        cacheLimitRef.current +
                                                        initialCacheLimit
                                                    );
                                                }
                                            }}
                                        >
                                            Load More
                                        </button>
                                    ) : null}
                                </Suspense>
                            </ErrorBoundary>
                        ) : null}
                    </div>
                </div>
                <div className="headless-shop-tags">
                    <h1>
                        Cannabis Dispensary in{' '}
                        {seoGeos ? seoGeos : niceName ? retailerName : ''} |{' '}
                        {orderText}
                    </h1>
                </div>
                {productPopupData ? (
                    <SingleProduct
                        productPopupData={productPopupData}
                        setProductPopupData={setProductPopupData}
                        specialProductsIds={filteredSpecialProductsIds}
                    />
                ) : null}
            </PriceRangeContext.Provider>
        </>
    );
};

export default Menu;
